import type { Vec2, WallGrid, WallMetrics } from '../types';
import type { ShapeDefinition } from './registry';
import { wallBoundsFor } from './registry';

/**
 * 60° rhombi on a sheared lattice — the classic isometric-cube tiling.
 *
 * A rhombus is a parallelogram, so it tiles by translation alone and needs no
 * alternating orientation. The gap is applied along the lattice vectors, scaled
 * by `1 / sin 60°`: the edge a neighbour shares runs at 60° to the direction
 * you move it in, so a shift of `g` along the lattice only opens `g · sin 60°`
 * of actual clearance.
 */

const SQRT3 = Math.sqrt(3);
const SIN60 = SQRT3 / 2;

/** Edge length of a rhombus whose overall width is `size`. */
function edgeFor(size: number): number {
  // Width spans one full edge plus the horizontal run of the slanted one.
  return (size * 2) / 3;
}

export const rhombusShape: ShapeDefinition = {
  id: 'rhombus',
  name: 'Rhombus',
  description: '60° rhombi on a sheared lattice, reading as stacked isometric cubes.',
  phase: 2,
  gridHint: 'Each row is shifted half a rhombus to the right by the lattice.',

  outline(size: number): Vec2[] {
    const edge = edgeFor(size);
    const halfHeight = (edge * SIN60) / 2;
    // Counter-clockwise, centred on the parallelogram's centroid.
    return [
      { x: -edge * 0.75, y: -halfHeight },
      { x: edge * 0.25, y: -halfHeight },
      { x: edge * 0.75, y: halfHeight },
      { x: -edge * 0.25, y: halfHeight },
    ];
  },

  layout(column: number, row: number, size: number, gap: number): Vec2 {
    const edge = edgeFor(size);
    const step = edge + gap / SIN60;
    return {
      x: edge * 0.75 + column * step + row * (step / 2),
      y: (edge * SIN60) / 2 + row * step * SIN60,
    };
  },

  metrics(grid: WallGrid, size: number, gap: number): WallMetrics {
    const edge = edgeFor(size);
    const step = edge + gap / SIN60;
    const bounds = wallBoundsFor(rhombusShape, grid, size, gap);

    return {
      columns: grid.columns,
      rows: grid.rows,
      width: bounds.width,
      height: bounds.height,
      pitchX: step,
      pitchY: step * SIN60,
      panelSize: size,
    };
  },
};
